import { z } from 'zod';

export const NoteTemplate = z.enum(['blank', 'ruled', 'grid']);
const Id = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/);
const Dimension = z.number().finite().min(100).max(3000);
const Element = z
  .object({
    id: z.string().min(1).max(100),
    type: z.enum([
      'freedraw',
      'text',
      'image',
      'rectangle',
      'ellipse',
      'diamond',
      'line',
      'arrow',
      'frame',
    ]),
    x: z.number().finite().min(-100000).max(100000),
    y: z.number().finite().min(-100000).max(100000),
    width: z.number().finite().min(0).max(100000),
    height: z.number().finite().min(0).max(100000),
  })
  .passthrough();
const File = z.object({
  id: z.string().min(1).max(100),
  mimeType: z.enum(['image/png', 'image/jpeg', 'image/webp', 'image/gif']),
  dataURL: z
    .string()
    .max(10 * 1024 * 1024)
    .regex(/^data:image\/(?:png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+={0,2}$/),
  created: z.number().finite(),
  lastRetrieved: z.number().finite().optional(),
});
export const NoteScene = z.object({
  elements: z.array(Element).max(6000),
  files: z.record(z.string().max(100), File),
  appState: z
    .object({
      scrollX: z.number().finite().optional(),
      scrollY: z.number().finite().optional(),
      zoom: z
        .object({ value: z.number().finite().min(0.1).max(30) })
        .optional(),
    })
    .default({}),
});
export const Notebook = z.object({
  id: Id,
  title: z.string().min(1).max(160),
  lessonId: z.string().nullable(),
  version: z.number().int().positive(),
  archived: z.boolean(),
  updatedAt: z.string(),
});
export const NotePageSummary = z.object({
  id: Id,
  notebookId: Id,
  title: z.string().max(160),
  position: z.number().int().nonnegative(),
  template: NoteTemplate,
  width: Dimension,
  height: Dimension,
  revision: z.number().int().positive(),
  pdf: z
    .object({ assetId: Id, page: z.number().int().min(1).max(300) })
    .nullable(),
  updatedAt: z.string(),
});
export const NotePage = NotePageSummary.extend({ scene: NoteScene });
export const NotebookDetail = z.object({
  notebook: Notebook,
  pages: z.array(NotePageSummary),
});
export const NotebookList = z.object({ notebooks: z.array(Notebook) });
export const AddedNotePage = NotebookDetail.extend({ pageId: Id });
export const CreateNotebookReq = z.object({
  title: z.string().trim().min(1).max(160),
  lessonId: z.string().nullable().optional(),
});
export const UpdateNotebookReq = z.object({
  baseVersion: z.number().int().positive(),
  title: z.string().trim().min(1).max(160).optional(),
  archived: z.boolean().optional(),
});
export const CreateNotePageReq = z.object({
  baseVersion: z.number().int().positive(),
  template: NoteTemplate.optional(),
  duplicate: Id.optional(),
});
export const SaveNotePageReq = z.object({
  baseRevision: z.number().int().positive(),
  title: z.string().trim().max(160),
  template: NoteTemplate,
  scene: NoteScene,
});
export const ReorderNotePagesReq = z.object({
  baseVersion: z.number().int().positive(),
  ids: z.array(Id).min(1).max(300),
});
export const DeleteNotePageReq = z.object({
  baseVersion: z.number().int().positive(),
  baseRevision: z.number().int().positive(),
});
export const PdfPages = z
  .array(z.object({ width: Dimension, height: Dimension }))
  .min(1)
  .max(300);
export type NoteScene = z.infer<typeof NoteScene>;
export type Notebook = z.infer<typeof Notebook>;
export type NotePage = z.infer<typeof NotePage>;
export type NotePageSummary = z.infer<typeof NotePageSummary>;
export type NotebookDetail = z.infer<typeof NotebookDetail>;
export type NoteTemplate = z.infer<typeof NoteTemplate>;
